import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, TextField, Button } from '@mui/material';
import { Balance as BalanceIcon } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { onDataUpdated } from '../../utils/dataBus';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

interface Ligne {
  numero_compte: string;
  libelle: string;
  debit: number;
  credit: number;
}

interface Ecriture {
  id: string;
  date_ecriture: string;
  journal: string;
  libelle: string;
  montant_total: number;
  statut: 'brouillard' | 'valide' | 'annule';
  lignes: Ligne[];
}

const STORAGE_KEY = 'comptaEBNL_ecritures_local';

type Row6 = {
  numero_compte: string;
  solde_ouv_debit: number;
  solde_ouv_credit: number;
  mvt_debit: number;
  mvt_credit: number;
  solde_clot_debit: number;
  solde_clot_credit: number;
};

const BalancePage: React.FC = () => {
  const [ecritures, setEcritures] = useState<Ecriture[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [accountFilter, setAccountFilter] = useState<string>('');
  const [generatedAt, setGeneratedAt] = useState<string>('');

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) { try { setEcritures(JSON.parse(raw)); } catch { setEcritures([]); } }
    const off = onDataUpdated(() => {
      const r = localStorage.getItem(STORAGE_KEY);
      if (r) { try { setEcritures(JSON.parse(r)); } catch {} }
    });
    return off;
  }, []);

  const tokens = useMemo(() => accountFilter.split(/[,;\s]+/).map(t => t.trim()).filter(Boolean), [accountFilter]);

  const rows6 = useMemo<Row6[]>(() => {
    const rowsMap: Record<string, Row6> = {};
    const inOpening = (d: string) => fromDate ? d < fromDate : false;
    const inMovement = (d: string) => {
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      if (!fromDate && !toDate) return true;
      return true;
    };
    for (const e of ecritures) {
      for (const l of e.lignes) {
        const n = l.numero_compte || '';
        if (tokens.length > 0 && !tokens.some(t => n.startsWith(t))) continue;
        if (!rowsMap[n]) rowsMap[n] = { numero_compte: n, solde_ouv_debit: 0, solde_ouv_credit: 0, mvt_debit: 0, mvt_credit: 0, solde_clot_debit: 0, solde_clot_credit: 0 };
        if (fromDate && e.date_ecriture < fromDate) {
          rowsMap[n].solde_ouv_debit += Number(l.debit) || 0;
          rowsMap[n].solde_ouv_credit += Number(l.credit) || 0;
        } else if (inMovement(e.date_ecriture)) {
          rowsMap[n].mvt_debit += Number(l.debit) || 0;
          rowsMap[n].mvt_credit += Number(l.credit) || 0;
        }
      }
    }
    // closing
    Object.values(rowsMap).forEach(r => {
      const net = (r.solde_ouv_debit - r.solde_ouv_credit) + (r.mvt_debit - r.mvt_credit);
      r.solde_clot_debit = net > 0 ? net : 0;
      r.solde_clot_credit = net < 0 ? -net : 0;
    });
    const arr = Object.values(rowsMap).sort((a, b) => a.numero_compte.localeCompare(b.numero_compte));
    setGeneratedAt(new Date().toLocaleString());
    return arr;
  }, [ecritures, fromDate, toDate, tokens]);

  const getExportCSV = () => {
    const header = 'compte,ouv_debit,ouv_credit,mvt_debit,mvt_credit,clot_debit,clot_credit,from,to,generated_at';
    const lines = [header, ...rows6.map(r => [r.numero_compte, r.solde_ouv_debit.toFixed(2), r.solde_ouv_credit.toFixed(2), r.mvt_debit.toFixed(2), r.mvt_credit.toFixed(2), r.solde_clot_debit.toFixed(2), r.solde_clot_credit.toFixed(2), fromDate, toDate, generatedAt].join(','))];
    return { filename: 'balance_6colonnes.csv', content: lines.join('\n') };
  };

  const getExportJSON = () => ({ filename: 'balance_6colonnes.json', content: JSON.stringify({ period: { from: fromDate, to: toDate }, generated_at: generatedAt, rows: rows6 }, null, 2) });

  const exportPDF = async () => {
    try {
      const pdfDoc = await PDFDocument.create();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const addPage = () => {
        const page = pdfDoc!.addPage([595, 842]);
        return page;
      };
      let page = addPage();
      const { width, height } = page.getSize();
      const draw = (text: string, x: number, y: number, size = 10) => page.drawText(text, { x, y, size, font, color: rgb(0,0,0) });
      // Header
      draw('Balance (6 colonnes)', 200, height - 40, 12);
      draw(`Période ${fromDate || '…'} — ${toDate || '…'}`, 40, height - 60, 9);
      draw(`Généré le ${generatedAt || new Date().toLocaleString()}`, 400, height - 60, 9);
      // Table header
      let y = height - 90;
      const cols = [40, 120, 180, 250, 320, 390, 460];
      const headers = ['Compte','SO Débit','SO Crédit','Mvt Débit','Mvt Crédit','SC Débit','SC Crédit'];
      headers.forEach((h, i) => draw(h, cols[i], y, 9));
      y -= 14;
      const lineHeight = 12;
      const flushRow = (r: Row6) => {
        const vals = [
          r.numero_compte,
          r.solde_ouv_debit.toFixed(2),
          r.solde_ouv_credit.toFixed(2),
          r.mvt_debit.toFixed(2),
          r.mvt_credit.toFixed(2),
          r.solde_clot_debit.toFixed(2),
          r.solde_clot_credit.toFixed(2),
        ];
        vals.forEach((v, i) => draw(String(v), cols[i], y, 9));
      };
      for (const r of rows6) {
        if (y < 60) {
          page = addPage();
          y = height - 60;
        }
        flushRow(r);
        y -= lineHeight;
      }
      const bytes = await pdfDoc.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'balance_6colonnes.pdf'; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export PDF: échec de génération');
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Balance (6 colonnes)
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Période {fromDate || '…'} — {toDate || '…'} • Généré le {generatedAt || '—'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField type="date" label="Du" InputLabelProps={{ shrink: true }} value={fromDate} onChange={e => setFromDate(e.target.value)} />
        <TextField type="date" label="Au" InputLabelProps={{ shrink: true }} value={toDate} onChange={e => setToDate(e.target.value)} />
        <TextField label="Comptes (séparés par ; , espace)" value={accountFilter} onChange={e => setAccountFilter(e.target.value)} placeholder="ex: 401; 606; 7" sx={{ minWidth: 360 }} />
        <ImportExportBar getExportCSV={getExportCSV} getExportJSON={getExportJSON} />
        <Button variant="contained" onClick={exportPDF}>Exporter PDF</Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BalanceIcon color="primary" />
            <Typography variant="h6">Balance des comptes</Typography>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Compte</TableCell>
                  <TableCell align="right">SO Débit</TableCell>
                  <TableCell align="right">SO Crédit</TableCell>
                  <TableCell align="right">Mvmt Débit</TableCell>
                  <TableCell align="right">Mvmt Crédit</TableCell>
                  <TableCell align="right">SC Débit</TableCell>
                  <TableCell align="right">SC Crédit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows6.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center">Aucune donnée pour la période/filtre</TableCell></TableRow>
                ) : (
                  rows6.map(r => (
                    <TableRow key={r.numero_compte} hover>
                      <TableCell>{r.numero_compte}</TableCell>
                      <TableCell align="right">{r.solde_ouv_debit.toFixed(2)}</TableCell>
                      <TableCell align="right">{r.solde_ouv_credit.toFixed(2)}</TableCell>
                      <TableCell align="right">{r.mvt_debit.toFixed(2)}</TableCell>
                      <TableCell align="right">{r.mvt_credit.toFixed(2)}</TableCell>
                      <TableCell align="right">{r.solde_clot_debit.toFixed(2)}</TableCell>
                      <TableCell align="right">{r.solde_clot_credit.toFixed(2)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BalancePage;